// Theme-aligned chip styles for data tables

export const timeChipSx = {
  height: 22,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
  bgcolor: 'rgba(31, 91, 64, 0.08)',
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.18)',
  '& .MuiChip-label': { px: 1 }
};

export const sequenceChipSx = {
  height: 22,
  fontWeight: 700,
  fontSize: '0.75rem',
  bgcolor: 'rgba(31, 91, 64, 0.06)',
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.14)'
};

export const busyChipSx = (busy) => ({
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  cursor: 'pointer',
  ...(busy
    ? {
        bgcolor: 'rgba(237, 108, 2, 0.12)',
        color: '#e65100',
        border: '1px solid rgba(237, 108, 2, 0.35)'
      }
    : {
        bgcolor: 'rgba(31, 91, 64, 0.08)',
        color: '#1f5b40',
        border: '1px solid rgba(31, 91, 64, 0.15)'
      })
});

export const statusChipSx = (active) => ({
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  minWidth: 85,
  ...(active
    ? {
        bgcolor: '#1f5b40 !important',
        color: '#fff !important',
        border: '1px solid #1f5b40 !important',
        '&:hover': { bgcolor: '#133a28 !important' },
        '& .MuiChip-label': { color: '#fff !important' }
      }
    : {
        bgcolor: 'rgba(31, 91, 64, 0.08) !important',
        color: '#5c6b63 !important',
        border: '1px solid rgba(31, 91, 64, 0.15) !important',
        '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.12) !important' },
        '& .MuiChip-label': { color: '#5c6b63 !important' }
      })
});

export const verifiedChipSx = (verified) => ({
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  '& .MuiChip-icon': { fontSize: 14 },
  ...(verified
    ? {
        bgcolor: 'rgba(31, 91, 64, 0.08)',
        color: '#1f5b40',
        border: '1px solid rgba(31, 91, 64, 0.22)',
        '& .MuiChip-icon': { color: '#359163' }
      }
    : {
        bgcolor: 'rgba(237, 108, 2, 0.1)',
        color: '#e65100',
        border: '1px solid rgba(237, 108, 2, 0.35)',
        '& .MuiChip-icon': { color: '#e65100' }
      })
});

export const mutedOutlineChipSx = {
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  bgcolor: 'rgba(31, 91, 64, 0.04)',
  color: '#5c6b63',
  border: '1px solid rgba(31, 91, 64, 0.15)',
  '& .MuiChip-icon': { color: '#1f5b40', fontSize: 14 }
};

export const tableIconActiveSx = {
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.2)',
  bgcolor: 'rgba(31, 91, 64, 0.06)',
  '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.12)' }
};

export const tableIconMutedSx = {
  color: '#9aa5a0',
  border: '1px solid rgba(31, 91, 64, 0.1)',
  bgcolor: 'transparent'
};

const chipBase = {
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem'
};

/** Offer type badges (Item, Category, Discount, etc.). */
export const offerTypeChipSx = {
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  letterSpacing: '0.01em',
  bgcolor: 'rgba(31, 91, 64, 0.08)',
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.18)',
  '& .MuiChip-label': { px: 1.25 }
};

export const couponCodeChipSx = {
  height: 24,
  fontWeight: 700,
  fontSize: '0.6875rem',
  fontFamily: 'ui-monospace, monospace',
  bgcolor: 'rgba(31, 91, 64, 0.04)',
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.18)'
};

/** Role / access-level badges — single brand palette (no per-role rainbow colors). */
export const roleChipSx = {
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  letterSpacing: '0.02em',
  bgcolor: 'rgba(31, 91, 64, 0.08)',
  color: '#1f5b40',
  border: '1px solid rgba(31, 91, 64, 0.18)',
  '& .MuiChip-label': { px: 1.25 }
};

/** Veg / non-veg dot indicator beside product name in tables */
const VEG_COLOR = '#1f5b40';
const NON_VEG_COLOR = '#b71c1c';

export const vegIndicatorSx = (isVeg) => ({
  mt: '3px',
  width: 14,
  height: 14,
  flexShrink: 0,
  border: `2px solid ${isVeg ? VEG_COLOR : NON_VEG_COLOR}`,
  borderRadius: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

export const vegIndicatorDotSx = (isVeg) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: isVeg ? VEG_COLOR : NON_VEG_COLOR
});

/** Order status badges — muted theme palette (no bright fill colors) */
export function normalizeOrderStatusKey(status) {
  const s = String(status ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();
  return s;
}

export const orderStatusChipSx = (status) => {
  const key = normalizeOrderStatusKey(status);
  const base = {
    height: 24,
    fontWeight: 600,
    fontSize: '0.6875rem',
    letterSpacing: '0.02em',
    textTransform: 'none',
    '& .MuiChip-label': { px: 1.1 }
  };

  if (key === 'delivered' || key === 'served') {
    return {
      ...base,
      bgcolor: '#1f5b40',
      color: '#fff',
      border: '1px solid #1f5b40'
    };
  }

  if (key === 'cancelled') {
    return {
      ...base,
      bgcolor: 'rgba(183, 28, 28, 0.1)',
      color: '#b71c1c',
      border: '1px solid rgba(183, 28, 28, 0.32)'
    };
  }

  if (key === 'rejected') {
    return {
      ...base,
      bgcolor: 'rgba(74, 20, 140, 0.08)',
      color: '#6a1b9a',
      border: '1px solid rgba(106, 27, 154, 0.28)'
    };
  }

  if (key === 'pending') {
    return {
      ...base,
      bgcolor: 'rgba(31, 91, 64, 0.04)',
      color: '#5c6b63',
      border: '1px solid rgba(31, 91, 64, 0.14)'
    };
  }

  if (
    ['accepted', 'preparing', 'ready', 'pickedup', 'outfordelivery'].includes(key)
  ) {
    return {
      ...base,
      bgcolor: 'rgba(31, 91, 64, 0.08)',
      color: '#1f5b40',
      border: '1px solid rgba(31, 91, 64, 0.2)'
    };
  }

  return {
    ...base,
    bgcolor: 'rgba(31, 91, 64, 0.04)',
    color: '#5c6b63',
    border: '1px solid rgba(31, 91, 64, 0.15)'
  };
};

/** Driver / account approval status */
export const approvalChipSx = (status) => {
  const s = String(status ?? 'pending').toLowerCase();
  const base = {
    height: 24,
    fontWeight: 600,
    fontSize: '0.6875rem',
    '& .MuiChip-icon': { fontSize: 14 }
  };

  switch (s) {
    case 'approved':
      return {
        ...base,
        bgcolor: '#1f5b40 !important',
        color: '#fff !important',
        border: '1px solid #1f5b40 !important',
        '& .MuiChip-label': { color: '#fff !important' },
        '& .MuiChip-icon': { color: '#fff !important', marginLeft: '6px !important' }
      };
    case 'rejected':
      return {
        ...base,
        bgcolor: 'rgba(198, 40, 40, 0.08) !important',
        color: '#b71c1c !important',
        border: '1px solid rgba(198, 40, 40, 0.28) !important',
        '& .MuiChip-label': { color: '#b71c1c !important' },
        '& .MuiChip-icon': { color: '#b71c1c !important', marginLeft: '6px !important' }
      };
    case 'suspended':
      return {
        ...base,
        bgcolor: 'rgba(31, 91, 64, 0.04) !important',
        color: '#5c6b63 !important',
        border: '1px solid rgba(31, 91, 64, 0.15) !important',
        '& .MuiChip-label': { color: '#5c6b63 !important' },
        '& .MuiChip-icon': { color: '#5c6b63 !important', marginLeft: '6px !important' }
      };
    case 'pending':
    default:
      return {
        ...base,
        bgcolor: 'rgba(31, 91, 64, 0.06) !important',
        color: '#1f5b40 !important',
        border: '1px solid rgba(31, 91, 64, 0.22) !important',
        '& .MuiChip-label': { color: '#1f5b40 !important' },
        '& .MuiChip-icon': { color: '#1f5b40 !important', marginLeft: '6px !important' }
      };
  }
};

/** Order payment status (paid / unpaid) */
export const paymentStatusChipSx = (status) => {
  const key = String(status ?? '').toLowerCase();
  const base = {
    height: 24,
    fontWeight: 600,
    fontSize: '0.6875rem',
    textTransform: 'capitalize',
    '& .MuiChip-label': { px: 1.1 }
  };

  if (key === 'paid') {
    return {
      ...base,
      bgcolor: '#1f5b40 !important',
      color: '#fff !important',
      border: '1px solid #1f5b40 !important'
    };
  }

  if (key === 'unpaid' || key === 'pending') {
    return {
      ...base,
      bgcolor: 'rgba(237, 108, 2, 0.1) !important',
      color: '#e65100 !important',
      border: '1px solid rgba(237, 108, 2, 0.35) !important'
    };
  }

  return {
    ...base,
    bgcolor: 'rgba(31, 91, 64, 0.06) !important',
    color: '#5c6b63 !important',
    border: '1px solid rgba(31, 91, 64, 0.15) !important'
  };
};

export const orderFilterChipSx = (selected) =>
  selected
    ? {
        height: 28,
        fontWeight: 600,
        fontSize: '0.75rem',
        bgcolor: '#1f5b40',
        color: '#fff',
        border: '1px solid #1f5b40',
        '&:hover': { bgcolor: '#133a28' }
      }
    : {
        height: 28,
        fontWeight: 500,
        fontSize: '0.75rem',
        bgcolor: 'background.paper',
        color: '#1f5b40',
        border: '1px solid rgba(31, 91, 64, 0.22)',
        '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.06)' }
      };

/** Highlight tick in product tables */
export const highlightCheckMarkSx = {
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: '#1f5b40',
  border: '1px solid #1f5b40',
  boxShadow: '0 1px 3px rgba(31, 91, 64, 0.25)'
};

/** Highlight yes/no in product tables */
export const highlightChipSx = (highlight) => ({
  height: 24,
  fontWeight: 600,
  fontSize: '0.6875rem',
  ...(highlight
    ? {
        bgcolor: '#1f5b40',
        color: '#fff',
        border: '1px solid #1f5b40'
      }
    : {
        bgcolor: 'rgba(31, 91, 64, 0.04)',
        color: '#5c6b63',
        border: '1px solid rgba(31, 91, 64, 0.15)'
      })
});

/** Offer lifecycle status (active / inactive / expired / upcoming). */
export const offerStatusChipSx = (status) => {
  switch (status) {
    case 'active':
      return {
        ...chipBase,
        bgcolor: '#1f5b40',
        color: '#fff',
        border: '1px solid #1f5b40'
      };
    case 'inactive':
      return {
        ...chipBase,
        bgcolor: 'rgba(31, 91, 64, 0.08)',
        color: '#5c6b63',
        border: '1px solid rgba(31, 91, 64, 0.15)'
      };
    case 'expired':
      return {
        ...chipBase,
        bgcolor: 'rgba(198, 40, 40, 0.1)',
        color: '#c62828',
        border: '1px solid rgba(198, 40, 40, 0.35)'
      };
    case 'upcoming':
      return {
        ...chipBase,
        bgcolor: 'rgba(237, 108, 2, 0.1)',
        color: '#e65100',
        border: '1px solid rgba(237, 108, 2, 0.35)'
      };
    default:
      return {
        ...chipBase,
        bgcolor: 'rgba(31, 91, 64, 0.08)',
        color: '#5c6b63',
        border: '1px solid rgba(31, 91, 64, 0.15)'
      };
  }
};
